




































import Vue from 'vue';
import { Copyable, Tooltip } from '@nimiq/vue-components';
import { defineComponent, computed, ref } from '@vue/composition-api';
import ShortAddress from './ShortAddress.vue';

export default defineComponent({
    props: {
        address: {
            type: String,
            required: true,
        },
        displayedCharacters: Number,
        copyable: {
            type: Boolean,
            default: false,
        },
        tooltipContainer: Object as () => Vue | { $el: HTMLElement },
        tooltipPosition: {
            type: String as () => 'top left' | 'top right' | 'bottom left' | 'bottom right',
            default: 'bottom right',
            validator: (val: string) => /^(?:top|bottom) (?:left|right)$/.test(val),
        },
        // Because the address in the tooltip is typically much longer than the short address, offset the tooltip
        // position by default.
        offsetTooltipPosition: {
            type: Boolean,
            default: true,
        },
    },
    setup(props, context) {
        const tooltip$ = ref<Tooltip>(null);
        const copyable$ = ref<Copyable>(null);

        const tooltipHorizontalPosition = computed(() => props.tooltipPosition.split(' ').pop());

        let showTooltipTimeout = -1;
        let currentShowTimestamp = -1;
        function showTooltip(delay = 0) {
            if (showTooltipTimeout !== -1) {
                // Showing tooltip is already scheduled. Keep the original delay.
                return;
            }
            showTooltipTimeout = window.setTimeout(() => {
                showTooltipTimeout = -1;
                if (!tooltip$.value || tooltip$.value.isShown) return;
                tooltip$.value.show();
                currentShowTimestamp = Date.now();
            }, delay);
        }
        function hideTooltip() {
            window.clearTimeout(showTooltipTimeout);
            showTooltipTimeout = -1;
            if (!tooltip$.value || !tooltip$.value.isShown) return;
            tooltip$.value.hide(/* force */ true);
            currentShowTimestamp = -1;
        }

        function scheduleShowTooltip() {
            // If the address is copyable, show the tooltip only after a delay, to wait for whether a copy is triggered
            // immediately which cancels the tooltip via hideTooltip. Notably, if the copy was triggered by a tap via
            // touchscreen, we want to display the copy tooltip immediately and skip the address tooltip which would
            // open very shortly by the mouseenter and focus happening just immediately before the click/copy.
            showTooltip(props.copyable ? 200 : 0);
        }

        function onClick() {
            if (props.copyable) {
                // Trigger copy.
                if (!copyable$.value) return;
                if (copyable$.value.$el) {
                    // Don't keep copyable focused after clicks.
                    (copyable$.value.$el as HTMLElement).blur();
                }
                copyable$.value.copy();
            } else {
                // Toggle the tooltip via our custom handlers.
                if (!tooltip$.value) return;
                if (tooltip$.value.isShown) {
                    if (Date.now() - currentShowTimestamp < 200) {
                        // Was just shown by focus or mouseenter which fire immediately before the click.
                        return;
                    }
                    hideTooltip();
                } else {
                    showTooltip();
                }
            }
        }

        function onCopy() {
            context.emit('copy');
            // Hide / cancel address tooltip and re-show it after the copy tooltip disappeared.
            hideTooltip();
            showTooltip(1200);
        }

        return {
            tooltip$,
            copyable$,
            tooltipHorizontalPosition,
            scheduleShowTooltip,
            hideTooltip,
            onClick,
            onCopy,
        };
    },
    components: {
        Tooltip,
        Copyable,
        ShortAddress,
    },
});
